/**
 * loadTracker.ts — "is the world actually loaded yet?" (the loading round).
 *
 * Every asynchronous world asset (landmark GLBs, the HDRI sky, car models)
 * registers its promise here. The race-start pipeline (RaceWarmup) asks how
 * many are still in flight so the loading splash can show REAL progress and
 * the countdown can wait for the last model instead of letting it pop in —
 * and compile its shaders — three seconds into the race.
 *
 * Generation-scoped: `beginGeneration()` is called at the top of every city
 * build, so a load still trickling in from a PREVIOUS city (the player backed
 * out and picked another) never counts toward the new one. Pure bookkeeping,
 * no DOM/three imports; unit-testable.
 */

let generation = 0;
let total = 0;
let settled = 0;
const waiters = new Set<() => void>();

/** Start counting for a fresh world; earlier in-flight loads are forgotten. */
export function beginGeneration(): number {
  generation++;
  total = 0;
  settled = 0;
  notify();
  return generation;
}

/** Register an in-flight asset. Returns the same promise for chaining. */
export function trackLoad<T>(p: Promise<T>): Promise<T> {
  const gen = generation;
  total++;
  const done = (): void => {
    if (gen !== generation) return; // belonged to an older world
    settled++;
    notify();
  };
  p.then(done, done);
  return p;
}

export function loadsPending(): number {
  return total - settled;
}

/** Progress for the current generation in [0,1] — 1 when nothing was tracked. */
export function loadsProgress(): number {
  return total === 0 ? 1 : settled / total;
}

export function loadsCounts(): { total: number; settled: number } {
  return { total, settled };
}

function notify(): void {
  for (const w of Array.from(waiters)) w();
}

/**
 * Resolve when every tracked load of the CURRENT generation has settled, or
 * after `timeoutMs` (a stuck CDN must never hold a race hostage). Resolves
 * `true` if everything really landed, `false` on timeout/generation change.
 */
export function loadsSettled(timeoutMs: number): Promise<boolean> {
  const gen = generation;
  if (loadsPending() === 0) return Promise.resolve(true);
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = (ok: boolean): void => {
      waiters.delete(check);
      if (timer !== null) clearTimeout(timer);
      resolve(ok);
    };
    const check = (): void => {
      if (gen !== generation) finish(false);
      else if (loadsPending() === 0) finish(true);
    };
    waiters.add(check);
    timer = setTimeout(() => finish(false), timeoutMs);
  });
}
